/* eslint-disable no-use-before-define */
import * as React from 'react'
import styles from './tabs.style.styl'
import cx from 'classnames'
import { isEqual, filter } from 'lodash'
import TabScrollButton from '@mui/material/TabScrollButton'
import MaterialTabs from '@mui/material/Tabs'
import MaterialTab, { TabProps as MaterialTabProps } from '@mui/material/Tab'
import MaterialBadge from '@mui/material/Badge'
import { CSSSelector } from '@src/utils/css.selector.helper'
import Tooltip from '@mui/material/Tooltip'

export interface TabProps extends MaterialTabProps {
  hide?: boolean | ((tab: TabProps) => boolean)
  value: React.ReactText
  disabled?: boolean
  className?: string
  classNameSelected?: string
  classNameLabel?: string
  classNameTabTitle?: string
  badge?: number
  label: React.ReactNode
  sublabel?: string
  tooltip?: string
  style?: React.CSSProperties
}

export const Tab: React.FC<TabProps> = ({
  className,
  classNameLabel,
  classNameSelected,
  classNameTabTitle,
  style,
  badge,
  value,
  label,
  tooltip,
  sublabel,
  ...rest
}: React.PropsWithChildren<React.PropsWithChildren<TabProps>>) => {
  return React.createElement<
    MaterialTabProps & { datavalue?: React.ReactText }
  >(MaterialTab, {
    ...rest,
    style: {
      ...{
        padding: '6px 25px 2px 16px',
        fontSize: '.8125em',
        lineHeight: '1.2em'
      },
      ...style
    },
    role: 'tab',
    datavalue: value,
    classes: {
      selected: cx(
        Tabs.CSSSelectors.tabSelected.value,
        styles.tabSelected,
        classNameSelected
      ),
      root: cx(Tabs.CSSSelectors.tabRoot.value, styles.tabRoot, className)
    },
    value,
    label: (
      <MaterialBadge
        color="error"
        classes={{
          badge: 'tab-badge'
        }}
        max={10000}
        className={cx(styles.badgePosition)}
        badgeContent={badge}
      >
        <div className={cx(styles.tabTitle, classNameTabTitle)}>
          <span className={Tabs.CSSSelectors.tabTitle.value}>
            <span
              role={'label'}
              className={cx(classNameLabel, {
                [styles.labelText]: tooltip
              })}
            >
              {label}
            </span>
            {tooltip && (
              <Tooltip title={tooltip}>
                <i
                  role={'tooltip'}
                  className={cx('material-icons', styles.tooltip)}
                >
                  help
                </i>
              </Tooltip>
            )}
          </span>
          <span
            role={'sub-label'}
            className={cx(styles.subTitle, Tabs.CSSSelectors.subTitle.value)}
          >
            <>{sublabel}</>
          </span>
        </div>
      </MaterialBadge>
    )
  })
}

export type TabVariant = 'standard' | 'scrollable' | 'fullWidth'

export interface TabsProps<T extends React.ReactText> {
  id?: string
  value: T
  onChange?: (tabIndex: T) => void
  variant?: TabVariant
  style?: React.CSSProperties
  alwaysChange?: boolean
  scrollButtons?: 'auto' | boolean
}

function Tabs<T extends React.ReactText>(
  props: React.PropsWithChildren<TabsProps<T>>
) {
  const {
    alwaysChange,
    value,
    onChange,
    children,
    style,
    id,
    variant,
    scrollButtons = 'auto'
  } = props
  const [activeTabIndex, setActiveTabIndex] = React.useState<T>(value)

  React.useEffect(() => {
    if (value !== null && !isEqual(value, activeTabIndex)) {
      setActiveTabIndex(value)
    }
  }, [value])

  function handleTabClick(event: React.ChangeEvent<{}>, index: T) {
    if (!alwaysChange && index === activeTabIndex) return
    setActiveTabIndex(index)
    if (typeof onChange === 'function') {
      onChange(index)
    }
  }

  function getConcernedTabs() {
    const tabs = filter<React.ReactNode & { props?: TabProps }>(
      React.Children.toArray(children),
      tab => {
        const { props = {} as TabProps } = tab || {}
        const { hide } = props
        return typeof hide === 'function' ? !hide(props) : !hide
      }
    )
    return tabs
  }

  return (
    <>
      <MaterialTabs
        style={style}
        id={id}
        value={activeTabIndex}
        onChange={handleTabClick}
        variant={variant}
        scrollButtons={scrollButtons}
        textColor="primary"
        ScrollButtonComponent={props =>
          !props.disabled ? (
            <TabScrollButton
              role={'tabScrollButton'}
              classes={{
                root:
                  props.direction === 'left'
                    ? styles.leftScrollButton
                    : styles.rightScrollButton
              }}
              {...props}
            />
          ) : null
        }
        classes={{
          indicator: cx(Tabs.CSSSelectors.indicator.value, styles.tabIndicator),
          flexContainer: cx(
            Tabs.CSSSelectors.flexContainer.value,
            styles.tabFlexContainer
          ),
          scrollButtons: cx(
            Tabs.CSSSelectors.scrollButtonsAuto.value,
            styles.tabScrollButtonsAuto
          ),
          scrollableX: cx(
            Tabs.CSSSelectors.scrollable.value,
            styles.tabScrollable
          )
        }}
      >
        {getConcernedTabs()}
      </MaterialTabs>
    </>
  )
}

Tabs.CSSSelectors = {
  indicator: new CSSSelector('tabIndicator'),
  flexContainer: new CSSSelector('tabFlexContainer'),
  scrollButtonsAuto: new CSSSelector('tabScrollButtons'),
  scrollable: new CSSSelector('tabScrollable'),
  tabRoot: new CSSSelector('tabItem'),
  tabSelected: new CSSSelector('tabSelected'),
  tabTitle: new CSSSelector('tabTitle'),
  subTitle: new CSSSelector('subLabel')
}

export default Tabs
